/*
 * Who owns the right edge — your instruction, 16 Aug 2026.
 *
 * ── The inconsistency you spotted, and what is actually wrong with it ────────────────────────────
 *
 * Three things open on the inline-end side of this app and they are three different components: the
 * interview record opens as an **inline peek** inside `.tf-with-peek`, the composer opens as a
 * **flyout** fixed to the edge, and the copilot opens as a **rail** in the shell. Read as a list of
 * surfaces that is arbitrary. Read as a list of *relationships* it is not, and the rule is worth
 * writing down because it is the thing that decides which one a new screen should reach for:
 *
 *   inline peek   the subject is a row in the list you are looking at
 *                 → the list narrows, nothing is covered, clicking another row swaps the subject
 *   flyout        the act was started from a header or a menu and has no row to sit beside
 *                 → nothing to sit next to, so it overlays; still non-modal
 *   copilot       the subject is the whole screen rather than one row
 *                 → ambient, and never the thing you came to do
 *
 * By that rule the interview record is correctly inline and the composer is correctly a flyout.
 * **So the surfaces were not the defect. The defect is that two of them can hold the same pixels at
 * once and nothing arbitrates**: open the composer with the copilot rail out and one covers the
 * other, with no rule about which wins and no way back to the one underneath.
 *
 * ── What this is ────────────────────────────────────────────────────────────────────────────────
 *
 * One register for the overlay surfaces, with a declared precedence, and exactly one of them
 * visible. The inline peek is deliberately **not** in it: it lives in the page grid rather than over
 * it, so it contends for no pixels and suppressing it would remove the list-beside-panel reading
 * that is the whole reason it is inline.
 *
 * The precedence is *an act displaces a reading*. Somebody who opens the composer has said what they
 * want the screen to be about; the copilot is a thing you consult, and consulting it is never more
 * urgent than the form you are in the middle of. It is **suppressed rather than closed**, so it
 * comes back with its transcript when the act finishes — closing it would throw away a conversation
 * to make room for a panel that is about to go away again.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

/** Ordered by precedence: later wins. Add a surface here and nowhere else. */
export const SIDE_SURFACES = ['copilot', 'flyout'] as const;
export type SideSurface = (typeof SIDE_SURFACES)[number];

interface Register {
  /** The surface currently entitled to the edge, or null when nothing has claimed it. */
  readonly top: SideSurface | null;
  readonly claim: (surface: SideSurface) => void;
  readonly release: (surface: SideSurface) => void;
}

const SideSurfaceContext = createContext<Register | null>(null);

export function SideSurfaceProvider({ children }: { children: ReactNode }) {
  /*
   * A count per surface rather than a set, because claims nest: a flyout that opens a second flyout
   * would otherwise release the edge when the inner one closed, and the outer one — still on screen
   * — would find the copilot drawn over it.
   */
  const [claims, setClaims] = useState<Readonly<Record<string, number>>>({});

  const claim = useCallback((surface: SideSurface) => {
    setClaims((was) => ({ ...was, [surface]: (was[surface] ?? 0) + 1 }));
  }, []);

  const release = useCallback((surface: SideSurface) => {
    setClaims((was) => {
      const next = (was[surface] ?? 0) - 1;
      if (next > 0) return { ...was, [surface]: next };
      const { [surface]: _gone, ...rest } = was;
      return rest;
    });
  }, []);

  const top = useMemo(() => {
    // Highest precedence with a live claim. `SIDE_SURFACES` is the order, read back to front.
    for (let at = SIDE_SURFACES.length - 1; at >= 0; at -= 1) {
      const surface = SIDE_SURFACES[at]!;
      if ((claims[surface] ?? 0) > 0) return surface;
    }
    return null;
  }, [claims]);

  const value = useMemo<Register>(() => ({ top, claim, release }), [top, claim, release]);

  return <SideSurfaceContext.Provider value={value}>{children}</SideSurfaceContext.Provider>;
}

/**
 * Claim the edge while mounted, and say whether you are the one who has it.
 *
 * Returns `false` for a surface something with higher precedence is holding. A caller that renders
 * `null` on `false` is *suppressed*, not closed — its state survives, which is what lets the
 * copilot come back mid-conversation.
 *
 * Outside the provider it returns `true` and registers nothing, on the same reasoning
 * `useCopilotSession` uses: the sign-in screen renders outside every provider, and a hook that threw
 * there would trade a layering rule for a blank page.
 */
export function useSideSurface(surface: SideSurface | null): boolean {
  const register = useContext(SideSurfaceContext);
  const { claim, release } = register ?? {};

  useEffect(() => {
    // `null` is a caller that is not on screen right now — the copilot on a screen nobody has
    // summoned it on. It has to be expressible, or the register would report the assistant holding
    // the edge everywhere and every flyout would be "displacing" something that was never there.
    if (!surface || !claim || !release) return;
    claim(surface);
    return () => release(surface);
  }, [surface, claim, release]);

  if (!register || !surface) return true;
  // Before the effect has run on the first render, `top` does not yet include this surface. Treat
  // "nothing has claimed" as ours rather than flashing the panel out for one frame.
  return register.top === null || register.top === surface;
}
